import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { postApi } from "../api";
import CoverPlaceholder from "../components/CoverPlaceholder";

const formatoValor = (valor) => {
    const num = Math.round(parseFloat(valor) || 0);
    if (!num) return "Gratis";
    return "$" + num.toLocaleString("es-CL");
};

const promedio = (nums) => nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;

// Distribución real de estrellas (5★..1★) a partir de las reseñas cargadas —
// reemplaza las barras fijas que antes venían con porcentajes inventados.
const distribucionEstrellas = (resenas) => {
    const total = resenas.length;
    return [5, 4, 3, 2, 1].map((estrellas) => {
        const count = resenas.filter((r) => r.estrellas === estrellas).length;
        return { estrellas, pct: total ? Math.round((count / total) * 100) : 0 };
    });
};

const inicialesDe = (nombre) => (nombre || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

const Estrellas = ({ n }) => (
    <span className="estrellas" aria-label={`${n} de 5 estrellas`}>
        {"★".repeat(n)}{"☆".repeat(5 - n)}
    </span>
);

// SVG inline porque esta app no carga Font Awesome (a diferencia de
// cl-doc-material/docente/alum, acá no hay ningún ícono font disponible).
const IconoFacebook = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M22 12.06C22 6.51 17.52 2 12 2S2 6.51 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z" />
    </svg>
);

// Comparte la URL pública y limpia del curso (sin el ?preview=..., que es un
// token de vista previa de 5 min con contenido sin publicar — nunca debe
// terminar en un link compartido) en el diálogo de compartir de Facebook.
//
// OJO: la URL que se le pasa al robot de Facebook NO puede ser
// window.location.origin (el dominio de esta misma app, cl-doc-web) — ese
// dominio corre "npm start" (ver compose.yml) y cae en el setupProxy.js de
// esta app, que arma una tarjeta genérica (no conoce SOCIALIMG). La imagen
// personalizada la arma server-doc (DAOSocialImagen/funSvgFinal), servida
// desde el dominio de la API — por eso acá se arma con REACT_APP_URL_API
// (le sacamos el "/api/web" final) en vez del origin del navegador.
const dominioApiDoc = (process.env.REACT_APP_URL_API || "").replace(/\/api\/web\/?$/, "");
const compartirEnFacebook = () => {
    const urlLimpia = dominioApiDoc + window.location.pathname;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(urlLimpia)}`;
    window.open(shareUrl, "compartirFacebook", "width=600,height=520,noopener,noreferrer");
};

// Agrupa nodos de cur_material (ver DAOCursosPublic.js#funCursoPublicArbolPorTipo)
// por la raíz del curso (lo enlazado en cur_mateconte, ej. "Introducción",
// "Ejercicios") de la que vienen — no por el padre inmediato, que muchas
// veces es solo la carpeta "Publicación" y no dice nada (un curso puede
// tener más de una raíz, cada una con su propia carpeta "Publicación", y
// todas se llamarían igual). Reutilizado tanto para "Requisitos" como
// "Descripción" y "Para quién".
const agruparPorContenido = (items) => {
    const grupos = [];
    const porRaiz = {};
    const ordenados = [...items].sort((a, b) => (a.secuencia || 0) - (b.secuencia || 0));
    for (const it of ordenados) {
        const clave = it.raiz_codigo || it.id_padre || "sin-raiz";
        if (!porRaiz[clave]) {
            porRaiz[clave] = { clave, titulo: it.raiz_descripcion || it.padre_descripcion || it.padre_codigo || "General", items: [], raizSecuencia: it.raiz_secuencia || 0 };
            grupos.push(porRaiz[clave]);
        }
        porRaiz[clave].items.push(it);
    }
    grupos.sort((a, b) => a.raizSecuencia - b.raizSecuencia);
    return grupos;
};

// it.fecha_publicacion solo viaja cuando el backend acepta un token de
// preview vigente (ver funCursoPublicArbolPorTipo con incluirNoPublicado);
// sin preview, la fila ni siquiera llega si no está publicada. Cuando llega
// null bajo preview, es contenido en borrador — se marca en rojo.
// "Lo que aprenderás" se muestra como una única lista (checklist estilo
// Udemy) con TODOS los ítems APCO/APAC/APHA del curso completo, sin agrupar
// en secciones separadas como Requisitos/Descripción/Para quién — pero cada
// bloque igual lleva su propia etiqueta de origen (raiz_descripcion) antes
// del contenido, para no perder de dónde sale cada uno cuando el curso tiene
// más de una raíz enlazada (ver GruposContenido más abajo, mismo dato).
const ListaAprendizajes = ({ items }) => (
    <div className="curso-aprend-lista">
        {items.map((it) => {
            const sinPublicar = !it.fecha_publicacion;
            return (
                <div key={it.id_materia} className={"curso-htmlcontent" + (sinPublicar ? " curso-htmlcontent-borrador" : "")}>
                    {it.raiz_descripcion && <p className="curso-htmlcontent-origen">{it.raiz_descripcion}</p>}
                    {sinPublicar && <span className="curso-badge-borrador">No publicado</span>}
                    <div dangerouslySetInnerHTML={{ __html: it.observ }} />
                </div>
            );
        })}
    </div>
);

const GruposContenido = ({ grupos }) => (
    <>
        {grupos.map((g) => (
            <div key={g.clave} className="curso-aprend-grupo">
                <h3 className="curso-aprend-grupo-titulo">{g.titulo}</h3>
                {g.items.map((it) => {
                    const sinPublicar = !it.fecha_publicacion;
                    return (
                        <div key={it.id_materia} className={"curso-htmlcontent" + (sinPublicar ? " curso-htmlcontent-borrador" : "")}>
                            {sinPublicar && <span className="curso-badge-borrador">No publicado</span>}
                            <div dangerouslySetInnerHTML={{ __html: it.observ }} />
                        </div>
                    );
                })}
            </div>
        ))}
    </>
);

// Arma el árbol anidado del temario a partir de las filas planas que trae
// funCursoPublicTemarioAuto (ver DAOCursosPublic.js) — cada fila ya trae
// id_padre, así que solo hay que agrupar por ese campo. Puede haber varias
// unidades raíz si el curso enlaza más de un nodo en cur_mateconte.
const construirArbolTemario = (filas) => {
    const porId = {};
    filas.forEach((f) => { porId[f.id_materia] = { ...f, hijos: [] }; });
    const raices = [];
    filas.forEach((f) => {
        const nodo = porId[f.id_materia];
        if (f.id_padre && porId[f.id_padre]) porId[f.id_padre].hijos.push(nodo);
        else raices.push(nodo);
    });
    const ordenar = (nodo) => {
        nodo.hijos.sort((a, b) => (a.secuencia || 0) - (b.secuencia || 0));
        nodo.hijos.forEach(ordenar);
    };
    // Las raíces no se ordenan por su "secuencia" propia (esa es la posición
    // que tenían en el árbol de material original, antes de enlazarse al
    // curso, y no dice nada acá) sino por "raiz_secuencia": el orden real
    // definido en cur_mateconte (el Orden/Subir/Bajar de
    // /cursos/cursoscontelist).
    raices.sort((a, b) => (a.raiz_secuencia || 0) - (b.raiz_secuencia || 0));
    raices.forEach(ordenar);
    return raices;
};

// Cuántas lecciones (CONTE) hay bajo una unidad, contando las de sus
// sub-unidades anidadas — mismo número que se muestra al lado del título de
// la sección (estilo "N clases" de plataformas tipo Udemy).
const contarLecciones = (nodo) => {
    let n = 0;
    for (const h of nodo.hijos) n += h.tipo === "CONTE" ? 1 : contarLecciones(h);
    return n;
};

// Minutos totales bajo una unidad, sumando las lecciones (CONTE) de sus
// sub-unidades anidadas — mismo recorrido que contarLecciones, pero sumando
// nodo.minutos en vez de contar 1. No hay un total cacheado en cur_material:
// cada lección tiene su propio minutos, y el total de cada padre se calcula
// acá recorriendo el árbol que ya trajo la consulta del temario.
const sumarMinutos = (nodo) => {
    let n = 0;
    for (const h of nodo.hijos) n += h.tipo === "CONTE" ? (h.minutos || 0) : sumarMinutos(h);
    return n;
};

const formatMinutos = (mins) => {
    if (!mins) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m} min`;
};

// Una unidad (tipo UNID) se muestra como sección tipo acordeón — clic para
// abrir/cerrar sus propias sub-unidades y lecciones anidadas, con la
// cantidad de clases al lado del título. Un contenido (tipo CONTE) es una
// lección de hoja: acá solo se lista su título (nunca el texto/observ) — el
// temario es el índice del curso, no debe mostrar el contenido en sí, eso se
// ve dictando el curso. Si está sin publicar (y estamos en vista previa),
// igual se marca con el badge para que el autor lo vea.
const TemarioNodo = ({ nodo, expandidos, onToggle, previewActivo }) => {
    const abierta = expandidos.has(nodo.id_materia);
    if (nodo.tipo === "CONTE") {
        const sinPublicar = !nodo.fecha_publicacion;
        const duracion = formatMinutos(nodo.minutos);
        return (
            <li className="curso-temario-leccion curso-temario-fila">
                <span className="curso-temario-titulo">
                    {nodo.descripcion}
                    {sinPublicar && previewActivo && <span className="curso-badge-borrador">No publicado</span>}
                </span>
                <span className="curso-temario-col-clases" aria-hidden="true"></span>
                <span className="curso-temario-col-minutos">{duracion}</span>
            </li>
        );
    }
    const nLecciones = contarLecciones(nodo);
    const duracionSeccion = formatMinutos(sumarMinutos(nodo));
    return (
        <li>
            <div
                className="curso-temario-fila curso-temario-fila-clic curso-temario-fila-seccion"
                role="button"
                tabIndex={0}
                aria-expanded={abierta}
                onClick={() => onToggle(nodo.id_materia)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(nodo.id_materia); } }}
            >
                <span className="curso-temario-icon">{abierta ? "▾" : "▸"}</span>
                <span className="curso-temario-titulo">{nodo.descripcion}</span>
                <span className="curso-temario-col-clases">
                    {nLecciones > 0 && `${nLecciones} clase${nLecciones !== 1 ? "s" : ""}`}
                </span>
                <span className="curso-temario-col-minutos">{duracionSeccion}</span>
            </div>
            {abierta && nodo.hijos.length > 0 && (
                <ul className="curso-temario-sub">
                    {nodo.hijos.map((h) => (
                        <TemarioNodo key={h.id_materia} nodo={h} expandidos={expandidos} onToggle={onToggle} previewActivo={previewActivo} />
                    ))}
                </ul>
            )}
        </li>
    );
};

const CursoDetalle = () => {
    const { idRs } = useParams();
    const [searchParams] = useSearchParams();
    const previewToken = searchParams.get("preview");
    const [previewActivo, setPreviewActivo] = useState(false);
    const [curso, setCurso] = useState(null);
    const [temarioAuto, setTemarioAuto] = useState([]);
    const [aprendizajes, setAprendizajes] = useState([]);
    const [requisitos, setRequisitos] = useState([]);
    const [paraQuien, setParaQuien] = useState([]);
    const [descripcion, setDescripcion] = useState([]);
    const [resenas, setResenas] = useState([]);
    const [incluye, setIncluye] = useState([]);
    const [expandidos, setExpandidos] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const toggleExpandido = (id) => {
        setExpandidos((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        setLoading(true);
        setError(false);
        postApi("/free/cursos/detalle", { idKey: idRs, previewToken })
            .then((data) => {
                const row = data.rows && data.rows[0];
                if (!row) { setError(true); return; }
                setCurso(row);
                setPreviewActivo(!!(data.dataObj && data.dataObj.preview));
                const filasTemario = (data.dataObj && data.dataObj.temarioAuto) || [];
                setTemarioAuto(filasTemario);
                setExpandidos(new Set());
                setAprendizajes((data.dataObj && data.dataObj.aprendizajes) || []);
                setRequisitos((data.dataObj && data.dataObj.requisitos) || []);
                setParaQuien((data.dataObj && data.dataObj.paraQuien) || []);
                setDescripcion((data.dataObj && data.dataObj.descripcion) || []);
                setResenas((data.dataObj && data.dataObj.resenas) || []);
                setIncluye((data.dataObj && data.dataObj.incluye) || []);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [idRs, previewToken]);

    if (loading) return <p className="estado-msg">Cargando curso...</p>;
    if (error || !curso) return (
        <div className="estado-msg">
            <p>No se encontró el curso.</p>
            <Link to="/">Volver al catálogo</Link>
        </div>
    );

    const titulo = curso.ram_descrip || curso.ram_sigla;

    // "Lo que aprenderás" (APCO/APAC/APHA), "Requisitos" (REQUE) y "Para
    // quién" (AUDI) vienen del mismo mecanismo genérico: nodos de
    // cur_material colgados como hijos de una unidad en cualquier parte del
    // árbol, agrupados por esa unidad (ver agruparPorContenido arriba y
    // DAOCursosPublic.js#funCursoPublicArbolPorTipo).
    const gruposRequisitos = agruparPorContenido(requisitos);
    const gruposParaQuien = agruparPorContenido(paraQuien);
    const gruposDescripcion = agruparPorContenido(descripcion);

    // "Contenido del curso": árbol completo de unidades a partir de lo
    // enlazado en cur_mateconte (ver funCursoPublicTemarioAuto) — no hace
    // falta enlazar cada carpeta/subcarpeta a mano.
    const arbolTemario = construirArbolTemario(temarioAuto);
    const totalUnidades = temarioAuto.filter((f) => f.tipo === "UNID").length;
    const totalLecciones = temarioAuto.filter((f) => f.tipo === "CONTE").length;
    const totalMinutos = temarioAuto.reduce((sum, f) => sum + (f.tipo === "CONTE" ? (f.minutos || 0) : 0), 0);
    const duracionTotal = formatMinutos(totalMinutos);

    // Rating, reseñas e instructor: todo viene de cur_matecurso /
    // cur_matecurso_resena (ver DAOCursosPublic.js). Si un curso todavía no
    // tiene nada cargado, se oculta esa parte de la página en vez de mostrar
    // una cifra o una reseña inventada — a diferencia de los bullets de
    // arriba, acá serían afirmaciones específicas y falsas, no relleno.
    const tieneRating = curso.rating_count > 0;
    const tieneResenas = resenas.length > 0;
    const ratingPromedio = tieneResenas ? promedio(resenas.map((r) => r.estrellas)) : Number(curso.rating_promedio) || 0;
    const barrasValoracion = distribucionEstrellas(resenas);
    const tieneInstructorStats = curso.instructor_rating > 0 || curso.instructor_cursos_count > 0;
    // "Este curso incluye": igual que Lo que aprenderás/Requisitos/etc, sale
    // del árbol de material (tipo INCLUYE bajo "Publicación", ver
    // funCursoPublicArbolPorTipo) — cada ítem ya trae su propio <ul><li>...
    // armado por el autor, se inserta tal cual en vez de mapear icono/texto.
    const tieneIncluye = curso.horas_video > 0 || incluye.length > 0;

    return (
        <div className="curso-detalle">
            {previewActivo && (
                <div className="curso-preview-banner">
                    Vista previa — acceso temporal de 5 minutos. Lo marcado en rojo aún no está publicado.
                </div>
            )}
            <div className="curso-hero">
                <div className="curso-hero-inner">
                    {curso.car_descrip && <span className="course-card-tag">{curso.car_descrip}</span>}
                    <h1>{titulo}</h1>
                    {tieneRating && (
                        <div className="curso-hero-rating">
                            <span className="curso-hero-rating-num">{Number(curso.rating_promedio).toFixed(1)}</span>
                            <Estrellas n={Math.round(curso.rating_promedio)} />
                            <span className="curso-hero-rating-count">
                                ({curso.rating_count.toLocaleString("es-CL")} valoraciones)
                                {curso.estudiantes_count > 0 && ` · ${curso.estudiantes_count.toLocaleString("es-CL")} estudiantes`}
                            </span>
                        </div>
                    )}
                    {curso.prof_nombre && curso.prof_nombre.trim() && (
                        <p className="curso-detalle-prof">Creado por {curso.prof_nombre}</p>
                    )}
                    <p className="curso-hero-meta">
                        Última actualización {curso.fecha_actualizacion || ""} · {curso.idioma || "Español"}
                    </p>
                </div>
            </div>

            <div className="curso-body">
                <div className="curso-main">
                    <section className="curso-detalle-section curso-aprenderas">
                        <h2>Lo que aprenderás</h2>
                        {aprendizajes.length > 0 && <ListaAprendizajes items={aprendizajes} />}
                    </section>

                    <section className="curso-detalle-section">
                        <h2>Requisitos</h2>
                        {gruposRequisitos.length > 0 && <GruposContenido grupos={gruposRequisitos} />}
                    </section>

                    <section className="curso-detalle-section">
                        <h2>Descripción</h2>
                        {gruposDescripcion.length > 0 ? (
                            <GruposContenido grupos={gruposDescripcion} />
                        ) : curso.observ ? (
                            <p>{curso.observ}</p>
                        ) : null}
                    </section>

                    {arbolTemario.length > 0 && (
                        <section className="curso-detalle-section">
                            <h2>Contenido del curso</h2>
                            <p className="curso-temario-resumen">
                                {totalUnidades} unidad{totalUnidades !== 1 ? "es" : ""}
                                {totalLecciones > 0 && ` · ${totalLecciones} lección${totalLecciones !== 1 ? "es" : ""}`}
                                {duracionTotal && ` · ${duracionTotal}`}
                            </p>
                            <ol className="curso-temario">
                                {arbolTemario.map((n) => (
                                    <TemarioNodo key={n.id_materia} nodo={n} expandidos={expandidos} onToggle={toggleExpandido} previewActivo={previewActivo} />
                                ))}
                            </ol>
                        </section>
                    )}

                    <section className="curso-detalle-section">
                        <h2>Para quién es este curso</h2>
                        {gruposParaQuien.length > 0 && <GruposContenido grupos={gruposParaQuien} />}
                    </section>

                    {curso.prof_nombre && curso.prof_nombre.trim() && (
                        <section className="curso-detalle-section curso-instructor">
                            <h2>Instructor</h2>
                            <div className="curso-instructor-card">
                                <CoverPlaceholder seed={"prof-" + curso.prof_nombre} texto={curso.prof_nombre} />
                                <div>
                                    <p className="curso-instructor-nombre">{curso.prof_nombre}</p>
                                    <p className="curso-instructor-rol">Profesor(a)</p>
                                    {tieneInstructorStats && (
                                        <p className="curso-instructor-stats">
                                            {curso.instructor_rating > 0 && `★ ${Number(curso.instructor_rating).toFixed(1)} valoración del instructor`}
                                            {curso.instructor_cursos_count > 0 && ` · ${curso.instructor_cursos_count} curso${curso.instructor_cursos_count !== 1 ? "s" : ""}`}
                                        </p>
                                    )}
                                    {curso.instructor_bio && (
                                        <p className="curso-instructor-bio">{curso.instructor_bio}</p>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

                    <section className="curso-detalle-section curso-resenas">
                        <h2>Opiniones de estudiantes</h2>
                        {tieneResenas ? (
                            <>
                                <div className="curso-resenas-resumen">
                                    <div className="curso-resenas-promedio">
                                        <span className="curso-resenas-promedio-num">{ratingPromedio.toFixed(1)}</span>
                                        <Estrellas n={Math.round(ratingPromedio)} />
                                        <span className="curso-hero-rating-count">Valoración del curso</span>
                                    </div>
                                    <div className="curso-resenas-barras">
                                        {barrasValoracion.map((b) => (
                                            <div className="curso-resenas-barra-fila" key={b.estrellas}>
                                                <span>{b.estrellas}★</span>
                                                <div className="curso-resenas-barra"><div style={{ width: b.pct + "%" }} /></div>
                                                <span className="curso-resenas-barra-pct">{b.pct}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="curso-resenas-lista">
                                    {resenas.map((r) => (
                                        <div className="curso-resena" key={r.id_resena}>
                                            <div className="curso-resena-avatar">{inicialesDe(r.autor)}</div>
                                            <div>
                                                <p className="curso-resena-nombre">{r.autor}</p>
                                                <Estrellas n={r.estrellas} />
                                                <p className="curso-resena-texto">{r.comentario}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="curso-resenas-vacio">Todavía no hay reseñas para este curso.</p>
                        )}
                    </section>

                    <Link to="/" className="volver-link">← Volver al catálogo</Link>
                </div>

                <aside className="curso-sidebar">
                    <div className="curso-compra-card">
                        <CoverPlaceholder seed={curso.id_rs} texto={titulo} />
                        <div className="curso-compra-body">
                            <p className="course-card-price curso-compra-precio">{formatoValor(curso.valor)}</p>
                            <button type="button" className="curso-btn curso-btn-primario">Comprar ahora</button>
                            <button type="button" className="curso-btn curso-btn-secundario">Añadir al carrito</button>
                            <button type="button" className="curso-btn curso-btn-facebook" onClick={compartirEnFacebook}>
                                <IconoFacebook /> Compartir en Facebook
                            </button>
                            {curso.garantia_dias > 0 && (
                                <p className="curso-compra-garantia">Garantía de reembolso de {curso.garantia_dias} días</p>
                            )}
                            {tieneIncluye && (
                                <>
                                    <p className="curso-compra-incluye-titulo">Este curso incluye:</p>
                                    {curso.horas_video > 0 && (
                                        <ul className="curso-compra-incluye">
                                            <li>🎥 {curso.horas_video} horas de video bajo demanda</li>
                                        </ul>
                                    )}
                                    {incluye.map((it) => {
                                        const sinPublicar = !it.fecha_publicacion;
                                        return (
                                            <div key={it.id_materia} className={"curso-compra-incluye" + (sinPublicar ? " curso-htmlcontent-borrador" : "")}>
                                                {sinPublicar && <span className="curso-badge-borrador">No publicado</span>}
                                                <div dangerouslySetInnerHTML={{ __html: it.observ }} />
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                            <div className="curso-compra-meta">
                                {curso.modalidad && <span className="course-card-badge">{curso.modalidad}</span>}
                                {curso.sed_descrip && <span className="course-card-badge">{curso.sed_descrip}</span>}
                                {curso.per_sigla && <span className="course-card-badge">{curso.per_sigla}</span>}
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default CursoDetalle;
